/**
 * Fetch API interceptor for CSV data
 *
 * Monkey-patches window.fetch to intercept CSV responses from network requests.
 * The original fetch behavior is preserved - we clone the response for interception.
 */

import type { InterceptionConfig } from '../types/config';
import type { DataSource } from '../types/api';
import type {
  Interceptor,
  InterceptionHandler,
  InterceptedCSV,
} from './types';
import {
  isCSVMimeType,
  isCSVFilename,
  looksLikeCSV,
  hashCSV,
  extractFilename,
  suggestTableName,
  matchesUrlPattern,
} from './types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Fetch interceptor class
 */
export class FetchInterceptor implements Interceptor {
  private originalFetch: typeof window.fetch;
  private active: boolean = false;
  private config: Required<InterceptionConfig>;
  private handler: InterceptionHandler;

  constructor(config: Required<InterceptionConfig>, handler: InterceptionHandler) {
    this.originalFetch = window.fetch.bind(window);
    this.config = config;
    this.handler = handler;
  }

  /**
   * Start intercepting fetch requests
   */
  start(): void {
    if (this.active) {
      console.warn('[FetchInterceptor] Already active');
      return;
    }

    console.log('[FetchInterceptor] Starting interception');

    const self = this;

    // Monkey-patch window.fetch
    window.fetch = async function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      // Call original fetch
      const response = await self.originalFetch(input, init);

      // Extract URL
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

      // Check if we should intercept this URL
      if (!self.shouldIntercept(url)) {
        return response;
      }

      // Check if response is CSV
      const contentType = response.headers.get('Content-Type');
      const contentDisposition = response.headers.get('Content-Disposition');

      if (self.isCSVResponse(contentType, url, contentDisposition)) {
        // Clone the response so we don't consume the original
        const clonedResponse = response.clone();

        // Process in background (don't block the original request)
        self.processResponse(clonedResponse, url, init?.method || 'GET', contentDisposition).catch(
          error => {
            console.error('[FetchInterceptor] Error processing response:', error);
          }
        );
      }

      // Return original response
      return response;
    };

    this.active = true;
  }

  /**
   * Stop intercepting and restore original fetch
   */
  stop(): void {
    if (!this.active) {
      return;
    }

    console.log('[FetchInterceptor] Stopping interception');

    // Restore original fetch
    window.fetch = this.originalFetch;
    this.active = false;
  }

  /**
   * Check if currently intercepting
   */
  isActive(): boolean {
    return this.active;
  }

  /**
   * Check if we should intercept this URL
   */
  private shouldIntercept(url: string): boolean {
    // Check ignore patterns first
    if (this.config.ignorePatterns.length > 0) {
      if (matchesUrlPattern(url, this.config.ignorePatterns)) {
        return false;
      }
    }

    // Check URL patterns
    return matchesUrlPattern(url, this.config.urlPatterns);
  }

  /**
   * Check if response looks like CSV
   */
  private isCSVResponse(
    contentType: string | null,
    url: string,
    contentDisposition: string | null
  ): boolean {
    // Check MIME type
    if (isCSVMimeType(contentType)) {
      return true;
    }

    // Check if MIME type is in our configured list
    if (contentType) {
      const normalized = contentType.toLowerCase().split(';')[0].trim();
      if (this.config.mimeTypes.some(type => normalized === type)) {
        return true;
      }
    }

    // Check filename in Content-Disposition
    const filename = extractFilename(url, contentDisposition);
    if (isCSVFilename(filename)) {
      return true;
    }

    // Check URL
    if (isCSVFilename(url)) {
      return true;
    }

    return false;
  }

  /**
   * Process a CSV response
   */
  private async processResponse(
    response: Response,
    url: string,
    method: string,
    contentDisposition: string | null
  ): Promise<void> {
    try {
      // Read response as text
      const data = await response.text();

      // Skip empty responses
      if (!data || data.trim().length === 0) {
        return;
      }

      // Final heuristic check - does it look like CSV?
      if (!looksLikeCSV(data)) {
        console.log('[FetchInterceptor] Data does not look like CSV, skipping');
        return;
      }

      // Extract metadata
      const filename = extractFilename(url, contentDisposition);
      const suggestedName = suggestTableName(filename);
      const contentType = response.headers.get('Content-Type') || undefined;

      // Create intercepted CSV object
      const intercepted: InterceptedCSV = {
        id: uuidv4(),
        data,
        size: data.length,
        source: {
          type: 'network',
          url,
          filename,
          method,
          contentType,
        },
        interceptedVia: 'fetch',
        timestamp: Date.now(),
        hash: hashCSV(data),
        suggestedName,
      };

      console.log('[FetchInterceptor] CSV intercepted:', {
        url,
        size: data.length,
        filename,
        suggestedName,
      });

      // Call handler
      await this.handler(intercepted);
    } catch (error) {
      console.error('[FetchInterceptor] Error processing CSV:', error);
    }
  }
}

/**
 * Create a fetch interceptor
 */
export function createFetchInterceptor(
  config: Required<InterceptionConfig>,
  handler: InterceptionHandler
): Interceptor {
  return new FetchInterceptor(config, handler);
}
